import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import {
    ArrowRight,
    Briefcase,
    Building2,
    Check,
    ChevronDown,
    Clock,
    Heart,
    LogOut,
    MapPin,
    Search,
    SlidersHorizontal,
    Star,
    TrendingUp,
    Users,
    X,
    Zap
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const JobListing = () => {
  const { isAuthenticated, user, token, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [userApplications, setUserApplications] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    search: '',
    location: '',
    jobType: '',
    experienceLevel: '',
    salaryRange: '',
    workMode: '',
    company: '',
    industry: '',
    skills: '',
    sortBy: 'newest'
  });

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    setFilters(prev => ({
      ...prev,
      search: queryParams.get('search') || '',
      location: queryParams.get('location') || '',
      jobType: queryParams.get('jobType') || '',
      experienceLevel: queryParams.get('experienceLevel') || '',
      salaryRange: queryParams.get('salaryRange') || '',
      workMode: queryParams.get('workMode') || '',
      company: queryParams.get('company') || '',
      industry: queryParams.get('industry') || '',
      skills: queryParams.get('skills') || '',
      sortBy: queryParams.get('sortBy') || 'newest'
    }));
  }, [location.search]);

  const jobTypes = [
    { value: '', label: 'All Job Types' },
    { value: 'full-time', label: 'Full-time' },
    { value: 'part-time', label: 'Part-time' },
    { value: 'contract', label: 'Contract' },
    { value: 'freelance', label: 'Freelance' },
    { value: 'internship', label: 'Internship' }
  ];

  const experienceLevels = [
    { value: '', label: 'All Experience Levels' },
    { value: 'entry', label: 'Entry Level (0-2 years)' },
    { value: 'mid', label: 'Mid Level (3-5 years)' },
    { value: 'senior', label: 'Senior Level (6-10 years)' },
    { value: 'expert', label: 'Expert Level (10+ years)' }
  ];

  const workModes = [
    { value: '', label: 'All Work Modes' },
    { value: 'remote', label: 'Remote' },
    { value: 'hybrid', label: 'Hybrid' },
    { value: 'onsite', label: 'On-site' }
  ];

  const salaryRanges = [
    { value: '', label: 'All Salaries' },
    { value: '0-50000', label: '$0 - $50K' },
    { value: '50000-75000', label: '$50K - $75K' },
    { value: '75000-100000', label: '$75K - $100K' },
    { value: '100000-150000', label: '$100K - $150K' },
    { value: '150000+', label: '$150K+' }
  ];

  // Helper to build full logo URL safely
  const getLogoUrl = (logo, placeholder = 'https://via.placeholder.com/80x80?text=Logo') => {
    if (!logo) return placeholder;

    let path = '';
    if (typeof logo === 'object' && logo.url) {
      path = logo.url;
    } else if (typeof logo === 'string') {
      path = logo;
    }

    if (!path) return placeholder;

    // If already absolute URL, return as-is
    if (/^https?:\/\//i.test(path)) return path;

    // Ensure leading slash
    if (!path.startsWith('/')) path = '/' + path;

    return `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${path}`;
  };

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'salary-high', label: 'Highest Salary' },
    { value: 'salary-low', label: 'Lowest Salary' },
    { value: 'relevance', label: 'Most Relevant' }
  ];

  useEffect(() => {
    if (authLoading) return;

    if (isAuthenticated && token) {
      fetchUserApplications();
    }
    
    fetchJobs();
  }, [authLoading, isAuthenticated, token]);

    const fetchUserApplications = async () => {
      try {
      const headers = isAuthenticated && token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/applications`, { headers });
      
      if (response.data?.data?.applications) {
        setUserApplications(response.data.data.applications.map(app => app.job?.id || app.job?._id).filter(Boolean));
      }
    } catch (error) {
      console.error('Error fetching user applications:', error);
    }
  };

  const fetchJobs = async (loadMore = false) => {
    try {
      setLoading(true);
      const page = loadMore ? currentPage + 1 : 1;
      
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.location) params.append('location', filters.location);
      if (filters.jobType) params.append('jobType', filters.jobType);
      if (filters.experienceLevel) params.append('experienceLevel', filters.experienceLevel);
      if (filters.workMode) params.append('workMode', filters.workMode);
      if (filters.salaryRange) params.append('salaryRange', filters.salaryRange);
      if (filters.industry) params.append('industry', filters.industry);
      if (filters.skills) params.append('skills', filters.skills);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      params.append('page', page.toString());
      params.append('limit', '12');

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/jobs?${params}`);

      if (response.data && response.data.success) {
        const newJobs = response.data.data.jobs || [];
        console.log(newJobs);
      if (loadMore) {
          setJobs(prev => [...prev, ...newJobs]);
          setFilteredJobs(prev => [...prev, ...newJobs]);
          setCurrentPage(page);
      } else {
          setJobs(newJobs);
          setFilteredJobs(newJobs);
          setCurrentPage(1);
        }
        
        setHasMore(newJobs.length === 12);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
        toast.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Reset pagination when filters change
    setCurrentPage(1);
    setHasMore(true);
    
    // Debounce the API call
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      fetchJobs();
    }, 500);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      location: '',
      jobType: '',
      experienceLevel: '',
      salaryRange: '',
      workMode: '',
      company: '',
      industry: '',
      skills: '',
      sortBy: 'newest'
    });
    setCurrentPage(1);
    setHasMore(true);
    fetchJobs();
  };

  const toggleSaveJob = (jobId) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to save jobs');
      return;
    }
    
    setSavedJobs(prev => 
      prev.includes(jobId) 
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
    
    toast.success(
      savedJobs.includes(jobId) ? 'Job removed from saved' : 'Job saved successfully'
    );
  };

  const formatSalary = (salary) => {
    if (!salary) return 'Salary not specified';

    const formatNumber = (num) => {
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
      return num.toString();
    };

    if (typeof salary === 'object') {
      if (salary.min && salary.max) {
        return `$${formatNumber(salary.min)} - $${formatNumber(salary.max)}`;
      }
      if (salary.min) {
        return `$${formatNumber(salary.min)}+`;
      }
      if (salary.max) {
        return `Up to $${formatNumber(salary.max)}`;
      }
      if (salary.negotiable) {
        return 'Negotiable';
      }
      // If it's an object but no min/max/negotiable, show currency/period if present
      if (salary.currency || salary.period) {
        return [salary.currency, salary.period].filter(Boolean).join(' ');
      }
      return 'Salary not specified';
    }

    if (typeof salary === 'string' || typeof salary === 'number') {
      return salary;
    }

    return 'Salary not specified';
  };

  const getTimeAgo = (dateString) => {
    try {
      const date = new Date(dateString);
    const now = new Date();
      const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
      if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
        return `${diffInDays}d ago`;
      }
    } catch (error) {
      return 'Recently';
    }
  };

  const formatLocation = (location) => {
    if (!location) return 'Location not specified';
    
    if (typeof location === 'string') {
      return location;
    }
    
    if (typeof location === 'object') {
      const { city, state, country, isRemote } = location;
      
      if (isRemote) {
        return 'Remote';
      }
      
      const parts = [];
      if (city) parts.push(city);
      if (state) parts.push(state);
      if (country && country !== 'United States') parts.push(country);
      
      return parts.length > 0 ? parts.join(', ') : 'Location not specified';
    }
    
    return 'Location not specified';
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchJobs(true);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const JobCard = ({ job, index }) => {
    const isApplied = userApplications.includes(job.id || job._id);
    const isSaved = savedJobs.includes(job.id || job._id);
    
    return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: index * 0.1 }}
        whileHover={{ y: -4 }}
        className={`card-luxury p-6 group cursor-pointer transition-all duration-300 ${
          viewMode === 'list' ? 'flex items-start space-x-6' : ''
        }`}
        onClick={() => navigate(`/job/${job.id || job._id}`)}
      >
        <div className={`${viewMode === 'list' ? 'flex-shrink-0' : 'mb-4'}`}>
          <img
            src={getLogoUrl(job.company?.logo)}
            alt={job.company?.name || 'Company'}
            className={`object-cover shadow-soft group-hover:shadow-lg transition-shadow ${
              viewMode === 'list' ? 'w-16 h-16 rounded-2xl' : 'w-20 h-20 rounded-2xl mx-auto'
            }`}
          />
                      </div>
                      
                      <div className="flex-1 min-w-0">
          <div className={`${viewMode === 'list' ? 'flex items-start justify-between' : ''}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                          {job.featured && (
                    <span className="inline-flex items-center px-2 py-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-medium rounded-full">
                              <Star className="w-3 h-3 mr-1" />
                              Featured
                            </span>
                          )}
                          {job.urgent && (
                    <span className="inline-flex items-center px-2 py-1 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-medium rounded-full">
                      <Zap className="w-3 h-3 mr-1" />
                              Urgent
                            </span>
                          )}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSaveJob(job.id || job._id);
                  }}
                  className={`p-2 rounded-xl transition-all duration-300 ${
                    isSaved 
                      ? 'text-red-500 bg-red-50 hover:bg-red-100' 
                      : 'text-slate-400 hover:text-red-500 hover:bg-red-50'
                  }`}
                >
                  <Heart className={`w-5 h-5 ${isSaved ? 'fill-current' : ''}`} />
                </button>
                        </div>
                        
              <h3 className={`font-heading font-bold text-slate-900 group-hover:gradient-text-primary transition-all duration-300 ${
                viewMode === 'list' ? 'text-xl mb-2' : 'text-lg mb-3'
              }`}>
                {job.title}
                        </h3>
                        
              <div className="space-y-2 mb-4">
                <div className="flex items-center space-x-2">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600 font-medium">{job.company?.name}</span>
                  {job.company?.verified && (
                    <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                      <Check className="w-2 h-2 text-white" />
                          </div>
                  )}
                          </div>
                
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{formatLocation(job.location)}</span>
                          </div>
                
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{job.workMode || 'Not specified'}</span>
                          </div>
                        </div>
                        
              {job.skills && job.skills.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                  {job.skills.slice(0, 3).map((skill, skillIndex) => (
                            <span
                      key={skillIndex}
                      className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium"
                            >
                      {skill}
                            </span>
                          ))}
                  {job.skills.length > 3 && (
                    <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
                      +{job.skills.length - 3} more
                            </span>
                          )}
                        </div>
              )}

              <div className={`flex items-center justify-between ${
                viewMode === 'list' ? 'mt-4' : ''
              }`}>
                <div className="text-lg font-bold gradient-text-primary">
                  {formatSalary(job.salary)}
                            </div>
                <div className="flex items-center space-x-2 text-sm text-slate-500">
                  <Clock className="w-4 h-4" />
                  <span>{getTimeAgo(job.createdAt || job.postedDate)}</span>
                        </div>
                      </div>
                    </div>
                    
            {viewMode === 'list' && (
              <div className="flex flex-col space-y-3 ml-6">
                {isApplied ? (
                  <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-xl font-medium">
                    <Check className="w-4 h-4 mr-2" />
                    Applied
                  </div>
                ) : (
                      <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/job/${job.id || job._id}`);
                    }}
                    className="btn-primary"
                  >
                    Apply Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                      </button>
                )}
                
                <div className="text-sm text-slate-500 text-center">
                  {job.applicants || 0} applicants
                    </div>
                  </div>
            )}
          </div>

          {viewMode === 'grid' && (
            <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
              {isApplied ? (
                <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-xl font-medium text-sm">
                  <Check className="w-4 h-4 mr-2" />
                  Applied
                </div>
              ) : (
              <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/job/${job.id || job._id}`);
                  }}
                  className="btn-primary text-sm px-6 py-2"
                >
                  Apply Now
                  <ArrowRight className="w-4 h-4 ml-2" />
              </button>
              )}
              
              <div className="text-sm text-slate-500">
                {job.applicants || 0} applicants
              </div>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <header className="nav-luxury sticky top-0 z-50">
        <div className="container-luxury">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-3 group">
                <div className="w-10 h-10 gradient-bg-luxury rounded-2xl flex items-center justify-center shadow-luxury group-hover:scale-105 transition-transform">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-display font-bold gradient-text">JobZentro</h1>
                  <p className="text-xs text-slate-500 font-medium">An AI HRMS Platform</p>
                </div>
              </Link>
              <nav className="hidden lg:flex space-x-8 ml-8">
                {isAuthenticated ? (
                  <>
                    <Link to="/dashboard" className="nav-link">Dashboard</Link>
                    <Link to="/jobs" className="nav-link active">Browse Jobs</Link>
                    <Link to="/applications" className="nav-link">Applications</Link>
                    <Link to="/profile" className="nav-link">Profile</Link>
                    <Link to="/about" className="nav-link">About</Link>
                    <Link to="/contact" className="nav-link">Contact</Link>
                  </>
                ) : (
                  <>
                    <Link to="/" className="nav-link">Home</Link>
                    <Link to="/jobs" className="nav-link active">Jobs</Link>
                    <Link to="/about" className="nav-link">About</Link>
                    <Link to="/contact" className="nav-link">Contact</Link>
                  </>
                )}
              </nav>
            </div>
            
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <>
                  <Link to="/profile" className="flex items-center space-x-3 p-2 rounded-xl hover:bg-slate-100 transition-colors">
                    <img
                      src={user?.profilePicture || `https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face`}
                      alt="Profile"
                      className="w-10 h-10 rounded-full object-cover shadow-soft"
                    />
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-heading font-semibold text-slate-900">
                      {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-slate-500">Job Seeker</p>
                    </div>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 transition-colors p-2 rounded-xl hover:bg-slate-100"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="hidden md:inline">Logout</span>
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                    Sign In
                  </Link>
                  <Link to="/register" className="btn-primary">
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="container-luxury py-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="text-center mb-8">
            <h1 className="text-4xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Discover Your
              <span className="block gradient-text">Dream Job</span>
          </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Explore thousands of opportunities from top companies and find the perfect role that matches your skills and ambitions.
            </p>
          </div>

          <div className="flex items-center justify-center space-x-8 text-sm text-slate-600">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>{filteredJobs.length} jobs available</span>
            </div>
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-4 h-4 text-slate-400" />
              <span>Updated daily</span>
            </div>
            <div className="flex items-center space-x-2">
              <Star className="w-4 h-4 text-amber-400" />
              <span>Top companies</span>
            </div>
          </div>
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="card-luxury p-8 mb-8"
        >
          {/* Main Search */}
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="relative">
              <Search className="absolute left-1 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Job title, company, or keywords..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="input-luxury pl-12"
              />
            </div>
            
            <div className="relative">
              <MapPin className="absolute left-1 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Location (City, State, Remote)"
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                className="input-luxury pl-12"
              />
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn-outline flex items-center justify-center space-x-2 ${
                showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : ''
              }`}
            >
              <SlidersHorizontal className="w-5 h-5" />
              <span>Advanced Filters</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Advanced Filters */}
          <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="border-t border-slate-200 pt-6"
            >
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <select
                    value={filters.jobType}
                    onChange={(e) => handleFilterChange('jobType', e.target.value)}
                    className="input-luxury"
                  >
                    {jobTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>

                  <select
                    value={filters.experienceLevel}
                    onChange={(e) => handleFilterChange('experienceLevel', e.target.value)}
                    className="input-luxury"
                  >
                    {experienceLevels.map(level => (
                      <option key={level.value} value={level.value}>{level.label}</option>
                    ))}
                  </select>

                  <select
                    value={filters.workMode}
                    onChange={(e) => handleFilterChange('workMode', e.target.value)}
                    className="input-luxury"
                  >
                    {workModes.map(mode => (
                      <option key={mode.value} value={mode.value}>{mode.label}</option>
                    ))}
                  </select>

                  <select
                    value={filters.salaryRange}
                    onChange={(e) => handleFilterChange('salaryRange', e.target.value)}
                    className="input-luxury"
                  >
                    {salaryRanges.map(range => (
                      <option key={range.value} value={range.value}>{range.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <input
                      type="text"
                      placeholder="Skills (e.g., React, Python, Marketing)"
                      value={filters.skills}
                      onChange={(e) => handleFilterChange('skills', e.target.value)}
                      className="input-luxury w-64"
                    />
                    <input
                      type="text"
                      placeholder="Industry"
                      value={filters.industry}
                      onChange={(e) => handleFilterChange('industry', e.target.value)}
                      className="input-luxury w-48"
                    />
              </div>

                <button
                  onClick={clearFilters}
                    className="btn-outline text-sm px-4 py-2"
                >
                    <X className="w-4 h-4 mr-1" />
                    Clear All
                </button>
              </div>
            </motion.div>
          )}
          </AnimatePresence>
        </motion.div>

        {/* Toolbar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-heading font-bold text-slate-900">
              {filteredJobs.length} Jobs Found
            </h2>
            {filters.search && (
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                "{filters.search}"
                          </span>
                        )}
                      </div>
                      
          <div className="flex items-center space-x-4">
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="input-luxury w-auto text-sm"
            >
              {sortOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            
            {/* <div className="flex items-center space-x-2 border border-slate-200 rounded-xl p-1">
                    <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-blue-500 text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Grid className="w-4 h-4" />
                    </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-blue-500 text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div> */}
                  </div>
        </motion.div>

        {/* Jobs Grid/List */}
        {loading && currentPage === 1 ? (
          <div className="text-center py-20">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-slate-600 font-medium">Loading amazing opportunities...</p>
                </div>
        ) : filteredJobs.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Search className="w-12 h-12 text-slate-400" />
            </div>
            <h3 className="text-2xl font-heading font-bold text-slate-900 mb-2">No jobs found</h3>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              We couldn't find any jobs matching your criteria. Try adjusting your filters or search terms.
            </p>
            <button
              onClick={clearFilters}
              className="btn-primary"
            >
              Clear Filters
              <X className="w-4 h-4 ml-2" />
            </button>
              </motion.div>
        ) : (
          <>
            <div className={`grid gap-6 ${
              viewMode === 'grid' 
                ? 'md:grid-cols-2 lg:grid-cols-3' 
                : 'grid-cols-1'
            }`}>
              {filteredJobs.map((job, index) => (
                <JobCard key={job.id || job._id} job={job} index={index} />
              ))}
        </div>

            {/* Load More */}
            {hasMore && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
                className="text-center mt-12"
          >
            <button 
              onClick={handleLoadMore}
              disabled={loading}
                  className="btn-outline text-lg px-12 py-4"
            >
              {loading ? (
                <>
                      <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin mr-2" />
                      Loading More...
                </>
              ) : (
                <>
                      Load More Jobs
                      <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </button>
          </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default JobListing; 